import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
}

const defaultMeta = {
  title: 'SkillSwipe - Learn Skills Together',
  description: 'Connect with peers who share your passion for learning. Teach what you know, learn what you want, and grow together through skill exchange and knowledge sharing.',
  image: 'https://skillswipe.co.in/pwa-512x512.png',
  url: 'https://skillswipe.co.in',
};

export const SEO = ({
  title = defaultMeta.title,
  description = defaultMeta.description,
  canonical,
  image = defaultMeta.image,
  type = 'website',
  noindex = false,
}: SEOProps) => {
  const fullTitle = title === defaultMeta.title ? title : `${title} | SkillSwipe`;
  const canonicalUrl = canonical || defaultMeta.url;

  // JSON-LD Structured Data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SkillSwipe',
    url: defaultMeta.url,
    logo: defaultMeta.image,
    description: defaultMeta.description,
    sameAs: [
      'https://twitter.com/skillswipe',
      'https://linkedin.com/company/skillswipe',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'SkillSwipe',
    url: defaultMeta.url,
    description: defaultMeta.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${defaultMeta.url}/explore?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'SkillSwipe',
    url: defaultMeta.url,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    description: defaultMeta.description,
    featureList: [
      'Skill Exchange',
      'Peer-to-Peer Learning',
      'Video Sessions',
      'Learning Circles',
      'Daily Challenges',
      'AI Learning Assistant',
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:site_name" content="SkillSwipe" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@skillswipe" />
      <meta name="twitter:creator" content="@skillswipe" />

      {/* Additional Meta Tags */}
      <meta name="application-name" content="SkillSwipe" />
      <meta name="apple-mobile-web-app-title" content="SkillSwipe" />
      <meta name="keywords" content="skill exchange, peer learning, teach skills, learn skills, knowledge sharing, online learning, skill swap, education platform" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webAppSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
